import {
  CAMPAIGN_CHANNELS,
  CAMPAIGN_CHANNEL_LABELS,
  messageTemplateInputSchema,
  type CampaignChannel,
  type MessageTemplate,
  type MessageTemplateInput,
} from '@aimmo/contracts';
import { Badge, defineResource, tsRestResourceClient } from '@aimmo/ui';
import { api } from '../lib/api.js';

/**
 * The message a campaign sends (M9 / P2).
 *
 * A template is written once and sent by any number of campaigns, which is the
 * whole reason it is a row rather than a field on the campaign: the same
 * welcome message goes out for a year while the audiences around it change.
 *
 * Nothing here delivers. P2 has no channel — the connector the agency holds
 * reports `bulk: false` — so a template is composed and waits for P3.
 */
const CHANNEL_TONE: Record<CampaignChannel, 'accent' | 'neutral'> = {
  email: 'accent',
  whatsapp: 'neutral',
};

export const messageTemplatesResource = defineResource<
  MessageTemplate,
  MessageTemplateInput,
  MessageTemplateInput,
  MessageTemplateInput
>({
  key: 'message-templates',
  label: { one: 'Template', many: 'Template', gender: 'm' },
  // The kit's adapter wants a router shaped `list/get/create/update/remove`,
  // and one contract module carries three resources — so the five routes are
  // handed over by name rather than the module as a whole.
  client: tsRestResourceClient({
    list: api.campaigns.listTemplates,
    get: api.campaigns.getTemplate,
    create: api.campaigns.createTemplate,
    update: api.campaigns.updateTemplate,
    remove: api.campaigns.removeTemplate,
  }),
  getRowId: (row) => row.id,
  defaultState: { sort: 'name', order: 'asc' },
  search: { placeholder: 'Cerca per nome…' },
  columns: [
    { id: 'name', header: 'Nome', sortKey: 'name' },
    {
      id: 'channel',
      header: 'Canale',
      width: '10rem',
      cell: ({ row }) => (
        <Badge tone={CHANNEL_TONE[row.channel]}>{CAMPAIGN_CHANNEL_LABELS[row.channel]}</Badge>
      ),
    },
    {
      id: 'subject',
      header: 'Oggetto',
      cell: ({ row }) => row.subject ?? <span className="text-muted">—</span>,
    },
  ],
  form: {
    schema: messageTemplateInputSchema,
    defaultValues: { name: '', channel: 'email', subject: '', body: '' },
    sections: [
      {
        id: 'main',
        title: 'Template',
        fields: [
          { name: 'name', label: 'Nome', type: 'text', required: true },
          {
            name: 'channel',
            label: 'Canale',
            type: 'select',
            required: true,
            options: CAMPAIGN_CHANNELS.map((channel) => ({
              value: channel,
              label: CAMPAIGN_CHANNEL_LABELS[channel],
            })),
          },
          // Required on email and refused on WhatsApp, which the schema
          // enforces: a WhatsApp message has no subject, and a field that
          // stored one would invite a screen to show it.
          { name: 'subject', label: 'Oggetto', type: 'text' },
          { name: 'body', label: 'Testo', type: 'textarea', required: true, rows: 8 },
        ],
      },
    ],
  },
  detail: {
    title: (row) => row.name,
    sections: [
      {
        id: 'main',
        title: 'Template',
        fields: [
          { id: 'name', label: 'Nome' },
          { id: 'channel', label: 'Canale', render: (row) => CAMPAIGN_CHANNEL_LABELS[row.channel] },
          { id: 'subject', label: 'Oggetto' },
          { id: 'body', label: 'Testo' },
        ],
      },
    ],
  },
});
